/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import {
	GA4_EVENT_REMOVE_FROM_CART,
	PAGE_DATA_LAYER,
	REMOVE_FROM_CART,
} from '@/data/constants/gtm';
import { getGTMRemoveFromCartEventData } from '@/data/events/data/gtm/RemoveFromCart';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { GTMRemoveFromCartPayload } from '@/data/types/GTM';
import { error as logError } from '@/data/utils/loggerUtil';
import { pickBy } from 'lodash';
import TagManager from 'react-gtm-module';

export const measure_GA4 = async (
	data: Awaited<ReturnType<typeof getGTMRemoveFromCartEventData>>
) => {
	const {
		partNumber: item_id,
		name: item_name,
		brand: item_brand,
		category: item_category,
		price,
		currency,
		quantity,
		affiliation,
		productPrice,
		keyword: item_variant,
		size: item_talla,
	} = data;
	const dataLayerName = PAGE_DATA_LAYER;
	const tagManagerArgs = {
		dataLayer: {
			event: GA4_EVENT_REMOVE_FROM_CART,
			ecommerce: {
				...pickBy({ currency, value: price }, Boolean),
				items: [
					{
						item_id,
						item_name,
						coupon: 'N/A',
						discount:
							(productPrice?.list ?? 0) > (productPrice?.offer ?? 0)
								? (productPrice?.list ?? 0) - (productPrice?.offer ?? 0)
								: 0,
						affiliation,
						item_brand,
						item_category,
						item_variant,
						price,
						currency,
						quantity,
						item_talla,
						hclMarketplaceSeller: affiliation,
					},
				],
				count_remove_from_cart: 1,
			},
		},
		dataLayerName,
	};
	TagManager.dataLayer(tagManagerArgs);
};

export const measure_UA = async (
	data: Awaited<ReturnType<typeof getGTMRemoveFromCartEventData>>
) => {
	const { name, currency, partNumber, price, quantity, affiliation } = data;
	const dataLayerName = PAGE_DATA_LAYER;
	const tagManagerArgs = {
		dataLayer: {
			event: REMOVE_FROM_CART,
			ecommerce: {
				currencyCode: currency,
				remove: {
					products: [
						{
							name,
							id: partNumber,
							...pickBy({ price, quantity }, Boolean),
							dimension9: affiliation,
						},
					],
				},
			},
		},
		dataLayerName,
	};
	TagManager.dataLayer(tagManagerArgs);
};

export const sendGTMRemoveFromCartEvent = async (payload: GTMRemoveFromCartPayload) => {
	const { settings } = payload;
	const { ga4, ua } = getGTMConfig(settings);

	if (ga4 || ua) {
		const data = await getGTMRemoveFromCartEventData(payload);

		if (ua) {
			try {
				await measure_UA(data);
			} catch (error) {
				logError(
					undefined,
					'RemoveFromCart: sendGTMRemoveFromCartEvent: measure_UA: error: %o',
					error
				);
			}
		}

		if (ga4) {
			try {
				await measure_GA4(data);
			} catch (error) {
				logError(
					undefined,
					'RemoveFromCart: sendGTMRemoveFromCartEvent: measure_GA4: error: %o',
					error
				);
			}
		}
	}
};
